import { maxRole, roleRank, type OrgKind, type Role, type Visibility } from './constants';
import { isGroupRef } from './refs';

/**
 * Access resolution, pure (no I/O). This is the x-ray applied to permissions
 * (MODEL.md §6): access is **derived, never stored on the item**, computed from
 * the graph each time, with every path shown so revocation stays legible. The
 * read layer gathers the rows in SQL; this module decides (CLAUDE.md: "Aggregation
 * gathers in SQL, decides in domain/").
 *
 * Two networks meet here:
 *   - the **work network**: a grant on an item cascades **down the decomposition**
 *     to its subtree, unless a node **breaks the chain** (`inheritsAccess=false`),
 *     below which ancestor grants stop (the filesystem / Confluence ACL pattern).
 *   - the **people network**: a person reaches any item shared with a group they
 *     **transitively** belong to; nesting carries a role, and you cannot gain
 *     authority by nesting upward (the role along a path is the min of its hops).
 *
 * Effective access = the **union over every path**, **highest role wins** - and a
 * path is always a GRANT (docs/CONTRACT_SPINE.md §3 as amended 2026-07-12, the
 * mint model; HS-F9): **seats contribute nothing here.** Seating mints a direct
 * `member` grant in the seating transaction and a held seat locks it (§2), so
 * "a party can always see their contract" holds by construction, not by
 * resolution. The deep, item-specific powers (accept/dispute/criteria, the
 * reserved requester acts) come from the **contract** (requester/contributor)
 * and are checked as party facts where they apply - they are never access.
 */

/** The minimum (weaker) of two roles, for "you cannot exceed either hop". */
function minRole(a: Role, b: Role): Role {
  return roleRank(a) <= roleRank(b) ? a : b;
}

// --- The work network: grant cascade with breakable inheritance --------------

/** One node in an item's ancestor set (the item itself + every item reachable by
 *  walking parent edges up), with its `inheritsAccess` flag. */
export interface ChainNode {
  readonly itemId: string;
  readonly inheritsAccess: boolean;
}

/** A hierarchical (parent) edge among the ancestor nodes: `childId`'s parent is
 *  `parentId`. With multi-parent decomposition the ancestors form a DAG, not a
 *  chain, so the cascade is a graph walk, not a linear walk up (docs/RELATIONSHIPS.md). */
export interface ParentEdge {
  readonly childId: string;
  readonly parentId: string;
  // Per-edge access inheritance (MODEL.md §6): when false, the child does not inherit
  // *this* parent's grants (a multi-parent child can break one edge and keep another).
  // Defaults true. Composed with the per-item `inheritsAccess` master break.
  readonly inherits?: boolean;
}

/** A role grant sitting on a specific item node (a `work_item_grants` row). */
export interface GrantEdge {
  readonly itemId: string;
  readonly granteeRef: string; // "person:<id>" or "group:<id>"
  readonly role: Role;
  /** The grant confers on the node's inheriting SUBTREE and not on the node
   *  itself: a rooted view's audience, attached at the root (docs/VIEWS.md
   *  §2b, 2026-09-14) - the root is the set's container, never a row of it,
   *  so sharing the list does not share the root. */
  readonly strictBelow?: boolean;
}

/** A grant resolved as in force on an item, with where it came from (provenance). */
export interface InheritedGrant {
  granteeRef: string;
  role: Role;
  sourceItemId: string; // the node the grant actually sits on
  inherited: boolean; // false = directly on the item; true = from an ancestor
}

/**
 * The grants in force on an item, walking its decomposition **DAG** upward. With
 * multi-parent decomposition an item can have several parents, so the cascade is a
 * graph walk (BFS from the item) rather than a linear walk up (docs/RELATIONSHIPS.md):
 *
 *   - the item's own grants always apply;
 *   - an ancestor's grants apply iff **some** unbroken path of parent edges reaches
 *     it from the item. A node with `inheritsAccess=false` does not inherit from its
 *     parents, so we never expand *above* it (its own grants still cascade down).
 *
 * `nodes` is the item + every ancestor (id + flag); `parentEdges` are the parent
 * edges among them. A grant is `inherited` when it sits on an ancestor, not the item.
 */
export function effectiveGrants(
  itemId: string,
  nodes: readonly ChainNode[],
  parentEdges: readonly ParentEdge[],
  grants: readonly GrantEdge[],
): InheritedGrant[] {
  if (nodes.length === 0) return [];
  const inherits = new Map(nodes.map((n) => [n.itemId, n.inheritsAccess]));
  const parentsOf = new Map<string, { parentId: string; inherits: boolean }[]>();
  for (const e of parentEdges) {
    const entry = { parentId: e.parentId, inherits: e.inherits ?? true };
    const arr = parentsOf.get(e.childId);
    if (arr) arr.push(entry);
    else parentsOf.set(e.childId, [entry]);
  }

  // BFS up the parent DAG, expanding a node's parents only while it inherits. The
  // per-item `inheritsAccess` is the master break (walk up no parent); the per-edge
  // `inherits` breaks a single parent (a multi-parent child can keep the others). The
  // reached set is exactly the nodes whose grants cascade to the item.
  const reached = new Set<string>([itemId]);
  const queue = [itemId];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    if (!(inherits.get(cur) ?? true)) continue; // per-item master break: walk up nothing
    for (const edge of parentsOf.get(cur) ?? []) {
      if (!edge.inherits) continue; // per-edge break: skip this one parent
      if (!reached.has(edge.parentId)) {
        reached.add(edge.parentId);
        queue.push(edge.parentId);
      }
    }
  }

  const out: InheritedGrant[] = [];
  for (const g of grants) {
    if (!reached.has(g.itemId)) continue;
    if (g.strictBelow && g.itemId === itemId) continue; // confers below, not here
    out.push({
      granteeRef: g.granteeRef,
      role: g.role,
      sourceItemId: g.itemId,
      inherited: g.itemId !== itemId,
    });
  }
  return out;
}

/**
 * What the access panel may say of the item an INHERITED grant sits on
 * (docs/MODEL.md "Discoverability and request-access", Leon 2026-10-05). The
 * panel names every person who can reach the item, since who can see an item
 * is that item's question; the source of their path is another item's:
 *
 *   - `open`: the viewer can open the source, so it shows as itself;
 *   - `stub`: a consented edge joins the source to the item the panel is on
 *     (its parent, for one), so it shows as its stub, its name, and the panel
 *     says no role its people hold there;
 *   - `unknown`: anything else (a grandparent with no edge to the item):
 *     nothing of it leaves the server, not its id and not its name, and the
 *     viewer may ask for access through the item (`requestInheritedAccess`,
 *     src/server/access.ts).
 *
 * `openable` holds the sources the viewer can open, `connected` the sources a
 * consented edge joins to the item. One rule for the read and the request.
 */
export type GrantSourceSight = 'open' | 'stub' | 'unknown';

export function grantSourceSight(
  sourceId: string,
  openable: ReadonlySet<string>,
  connected: ReadonlySet<string>,
): GrantSourceSight {
  if (openable.has(sourceId)) return 'open';
  return connected.has(sourceId) ? 'stub' : 'unknown';
}

/** A group that reaches an item (docs/MODEL.md §6): a group whose grant is in
 *  force on it, on the item itself or `inherited` from an ancestor. An item
 *  has no group of its own, only grants. */
export interface ReachingGroup {
  id: string;
  name: string;
  /** false: a grant of this group sits on the item itself; true: only on an
   *  ancestor, in force here through the cascade. */
  inherited: boolean;
}

/**
 * The groups that reach an item, from the grants in force on it
 * (`effectiveGrants`, the same resolution the item's reach line reads). Each
 * group once: direct when any of its grants sits on the item itself, else
 * inherited. Direct groups first, then inherited, each by name; never by grant
 * order, so an old grant cannot pass for the item's one group. A group with
 * no entry in `names` (no row) is left out. Pure.
 */
export function reachingGroupsOf(
  grants: readonly InheritedGrant[],
  names: ReadonlyMap<string, string>,
): ReachingGroup[] {
  const inheritedOnly = new Map<string, boolean>();
  for (const g of grants) {
    if (!isGroupRef(g.granteeRef)) continue;
    const id = g.granteeRef.slice('group:'.length);
    inheritedOnly.set(id, (inheritedOnly.get(id) ?? true) && g.inherited);
  }
  const out: ReachingGroup[] = [];
  for (const [id, inherited] of inheritedOnly) {
    const name = names.get(id);
    if (name !== undefined) out.push({ id, name, inherited });
  }
  return out.sort(
    (a, b) =>
      Number(a.inherited) - Number(b.inherited) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
}

// --- The people network: transitive group membership -------------------------

/** A membership edge: `memberRef` is a member of `groupRef`, with `role` inside it. */
export interface MembershipEdge {
  readonly groupRef: string; // "group:<id>"
  readonly memberRef: string; // "person:<id>" or "group:<id>" (nesting)
  readonly role: Role;
}

// You hold full authority over yourself (a person is a group of one, MODEL §3).
const SELF_ROLE: Role = 'owner';

/**
 * The role an actor holds in each group they **transitively** belong to. Max over
 * all paths to a group; along a single path the role is the **min of each hop**
 * (nesting upward cannot grant more than you hold below, nor more than the hop
 * grants). Iterative relaxation, bounded so an accidental membership cycle cannot
 * loop forever (nesting is acyclic by governance, MODEL §5).
 *
 * `seeds` narrows the actor (docs/AGENT_ACCESS.md §4): only their direct
 * memberships of those group refs start a path. Absent = every membership.
 * Called only through `principalReach` (src/read/access-inputs.ts), which
 * supplies the request's narrowing; `scripts/check-principal-reach.ts` fails
 * any other call.
 */
export function reachableGroups(
  actorRef: string,
  edges: readonly MembershipEdge[],
  seeds?: ReadonlySet<string>,
): Map<string, Role> {
  const best = new Map<string, Role>();
  const roleAt = (ref: string): Role | null =>
    ref === actorRef ? SELF_ROLE : (best.get(ref) ?? null);

  let changed = true;
  let guard = edges.length + 1; // a DAG settles in one pass; the bound caps cycles
  while (changed && guard-- > 0) {
    changed = false;
    for (const e of edges) {
      // A narrowed principal (docs/AGENT_ACCESS.md §4 rule 1): of the actor's
      // OWN membership edges only those into a seed group count; nesting above
      // a seed is followed as for anyone.
      if (seeds && e.memberRef === actorRef && !seeds.has(e.groupRef)) continue;
      const memberRole = roleAt(e.memberRef);
      if (memberRole === null) continue; // actor cannot (yet) reach this member
      const contributed = minRole(memberRole, e.role);
      const cur = best.get(e.groupRef) ?? null;
      const next = maxRole(cur, contributed);
      if (next !== cur) {
        best.set(e.groupRef, next!);
        changed = true;
      }
    }
  }
  return best;
}

/** One transitive member of a group, with the provenance of how they reach it. */
export interface EffectiveMember {
  ref: string; // "person:<id>" | "group:<id>"
  role: Role; // effective role in the root group (max over paths; min of each hop)
  direct: boolean; // a direct membership edge of the root group
  via: string[]; // direct child-group refs of the root this member is also reached through
}

/**
 * Expand a group's membership **downward**: every entity that transitively belongs to
 * `rootRef`, with its effective role (max over paths, min of each hop - the mirror of
 * `reachableGroups`, which walks upward) and its provenance: whether it is a **direct**
 * member, and which of the root's direct child-groups it is reached **via**. This is what
 * lets the access UI say "Ari has access directly" vs "…via Platform". Pure; bounded
 * against accidental membership cycles, like the upward closure.
 */
export function expandMembership(
  rootRef: string,
  edges: readonly MembershipEdge[],
): EffectiveMember[] {
  // Downward closure from one group: the effective role each reachable ref holds under it
  // (max over paths, min of each hop). Excludes the start group itself.
  const closureFrom = (startRef: string): Map<string, Role> => {
    const best = new Map<string, Role>();
    const roleAt = (ref: string): Role | null =>
      ref === startRef ? SELF_ROLE : (best.get(ref) ?? null);
    let changed = true;
    let guard = edges.length + 1; // a DAG settles in one pass; the bound caps cycles
    while (changed && guard-- > 0) {
      changed = false;
      for (const e of edges) {
        const groupRole = roleAt(e.groupRef);
        if (groupRole === null) continue; // not (yet) reached from the start group
        const contributed = minRole(groupRole, e.role);
        const cur = best.get(e.memberRef) ?? null;
        const next = maxRole(cur, contributed);
        if (next !== cur) {
          best.set(e.memberRef, next!);
          changed = true;
        }
      }
    }
    best.delete(startRef);
    return best;
  };

  const effective = closureFrom(rootRef);
  const directRefs = new Set(edges.filter((e) => e.groupRef === rootRef).map((e) => e.memberRef));
  // Each direct child-group of the root defines a "via" provenance bucket.
  const reachedVia = new Map<string, Set<string>>();
  for (const childGroup of directRefs) {
    if (!childGroup.startsWith('group:')) continue;
    for (const ref of closureFrom(childGroup).keys()) {
      (reachedVia.get(ref) ?? reachedVia.set(ref, new Set()).get(ref)!).add(childGroup);
    }
  }

  return [...effective.entries()].map(([ref, role]) => ({
    ref,
    role,
    direct: directRefs.has(ref),
    via: [...(reachedVia.get(ref) ?? [])],
  }));
}

// --- The union: a viewer's effective role on an item -------------------------

/** One way a viewer reaches an item, and the role that path contributes. Both
 *  kinds are GRANT paths - seats are not paths (HS-F9; the §2 mint turns a
 *  seating into the `direct` kind here). */
export interface AccessPath {
  kind: 'direct' | 'group';
  /** the grantee ref (direct) or the group ref (group). */
  via?: string;
  /** the node the grant sits on. */
  sourceItemId?: string;
  /** true if the grant was inherited from an ancestor. */
  inherited?: boolean;
  role: Role;
}

/**
 * A viewer's effective role on an item: the union over every access path, with
 * the highest role winning, and **all** paths returned (showing all paths is what
 * makes revocation legible, MODEL.md §6). `role` is null when no path reaches.
 *
 * Grants alone resolve here (docs/CONTRACT_SPINE.md §3, amended 2026-07-12; the
 * ban HS-F9): this function deliberately takes NO seat facts. A seated party
 * reaches their item through the direct member grant the seating minted (§2),
 * never through the seat itself.
 */
export function resolveViewerRole(params: {
  viewerRef: string;
  inheritedGrants: InheritedGrant[];
  groupsOfViewer: ReadonlyMap<string, Role>; // from principalReach(…)
  /** Whether the viewer's DIRECT paths count: a grant to the viewer's own
   *  ref. False only for a narrowed principal without own items
   *  (docs/AGENT_ACCESS.md §4 rule 2). */
  own: boolean;
}): { role: Role | null; paths: AccessPath[] } {
  const { viewerRef, inheritedGrants, groupsOfViewer, own } = params;
  const paths: AccessPath[] = [];

  for (const g of inheritedGrants) {
    if (g.granteeRef === viewerRef) {
      if (!own) continue; // a narrowed principal without own items
      // A direct grant to this person.
      paths.push({
        kind: 'direct',
        via: g.granteeRef,
        sourceItemId: g.sourceItemId,
        inherited: g.inherited,
        role: g.role,
      });
    } else if (isGroupRef(g.granteeRef)) {
      const inGroup = groupsOfViewer.get(g.granteeRef);
      if (inGroup) {
        // Via a group you belong to: bounded by your role in the group and the
        // group's role on the item.
        paths.push({
          kind: 'group',
          via: g.granteeRef,
          sourceItemId: g.sourceItemId,
          inherited: g.inherited,
          role: minRole(inGroup, g.role),
        });
      }
    }
  }

  let role: Role | null = null;
  for (const p of paths) role = maxRole(role, p.role);
  return { role, paths };
}

// --- Visibility enforcement (the only hard gate) -----------------------------

/** What a viewer may see of an item. `full` is the contents; `border` is the soft
 *  boundary (name + one-hop ring + request access, never the contents), a
 *  **jurisdiction-only** lane since docs/COMMONS.md; `denied` is nothing at all
 *  (not even that the item exists). */
export type Visible = 'full' | 'border' | 'denied';

/**
 * Visibility for a viewer (MODEL.md §6, split on the org kind by docs/COMMONS.md).
 * A viewer with **no standing** in the item's org sees `denied`, regardless of
 * open/protected. With standing:
 *   - a **path that reaches this item** (a role: party, group, or direct grant)
 *     always yields `full`;
 *   - an **open** item is `full` (open = visible to everyone with standing,
 *     logged: the org in a jurisdiction, everyone on Tesserano in the commons);
 *   - a **protected, pathless** item splits on the org kind. In a
 *     **jurisdiction** it is the soft `border` (the trusted-room discoverability:
 *     standing there is vetted membership). In the **commons** standing is
 *     universal, so a border would leak every title platform-wide to anyone who
 *     learns an id: it is `denied` outright. Discoverability in the commons is
 *     invitation, not browsing.
 *
 * Takes the org kind and the raw membership fact so the standing AND border
 * rules resolve **inside this one function** (via `standsInOrg` below) and
 * cannot fork across choke points. `viewerRole` is the resolved role on *this*
 * item; `memberOfAnyGroup` is whether the viewer transitively belongs to any
 * group of the item's org.
 */
export function visibilityFor(
  visibility: Visibility,
  viewerRole: Role | null,
  orgKind: OrgKind,
  memberOfAnyGroup: boolean,
  own: boolean,
): Visible {
  if (viewerRole !== null) return 'full'; // an explicit path to this item
  if (!standsInOrg(orgKind, memberOfAnyGroup, own)) return 'denied'; // no standing: the wall holds
  if (visibility === 'open') return 'full'; // opened up to everyone with standing
  // Protected + pathless: the border is the jurisdiction's lane only. Bare
  // commons standing never yields a border (docs/COMMONS.md, settled 2026-07-10).
  return orgKind === 'jurisdiction' ? 'border' : 'denied';
}

/**
 * Whether a viewer with this standing may ACT on the item - the write half of
 * the access law (MODEL.md §6/§7, the role ladder; Leon, 2026-09-07: an
 * explicit view-only path SEES and is denied every act). Full visibility,
 * minus the Read rung: a `viewer` role acts only where standing alone would
 * already let them - open work in an org they stand in - so the grant never
 * takes away what membership gives, and never adds a write it did not name.
 * Out-of-role acts among WRITERS stay flagged, not blocked (the state
 * machine); this is the rung below writing. The read grade and the spine's
 * gate (`gateForWrite`) both read this rule as the grade `act` of the SQL
 * sight predicate (`sight_standings`, `sight_access`,
 * drizzle/0106_sight_access.sql), held equal to the app's reference gate that
 * calls this function (src/test-support/sight-oracle.ts) by
 * src/read/sight-sql.integration.test.ts, so a surface never offers what the
 * spine refuses.
 */
export function mayAct(
  visibility: Visibility,
  viewerRole: Role | null,
  orgKind: OrgKind,
  memberOfAnyGroup: boolean,
  own: boolean,
): boolean {
  if (visibilityFor(visibility, viewerRole, orgKind, memberOfAnyGroup, own) !== 'full') {
    return false;
  }
  if (viewerRole !== 'viewer') return true;
  return visibility === 'open' && standsInOrg(orgKind, memberOfAnyGroup, own);
}

/**
 * Org standing (docs/COMMONS.md): whether a viewer counts as "a member of the org"
 * for the wall above. In a **jurisdiction**, standing is transitive membership of
 * any of the org's groups (the original semantics). In the **commons**, every
 * authenticated principal has standing - the viewer ref handed to the resolvers was
 * already resolved from a real session upstream, so "authenticated" is a given here.
 *
 * This is the single definition of the split in the app; the app's choke points
 * (`getVisibleItem`'s border, the write spine's `requireOrgStanding`, the
 * reference gate of src/test-support/sight-oracle.ts) resolve standing through
 * it, and the SQL sight predicate's `stands` (drizzle/0103 to 0107, which every
 * read and the spine's item gates read) is held equal to it by
 * src/read/sight-sql.integration.test.ts, so the rule cannot fork.
 * `visibilityFor` above calls it too, feeding the same (kind, membership) pair it
 * uses for the border split.
 *
 * `own` is false only for a narrowed principal without own items
 * (docs/AGENT_ACCESS.md §4 rule 3, ruling R3): commons standing belongs to
 * the person, not to any group, so such a principal has none there.
 * `memberOfAnyGroup` is then already the membership of the allowed groups
 * (`principalReach`), which is jurisdiction standing under the subset.
 */
export function standsInOrg(kind: OrgKind, memberOfAnyGroup: boolean, own: boolean): boolean {
  return kind === 'commons' ? own : memberOfAnyGroup;
}

/**
 * The facts a FLOW-role decision needs (docs/VIEWS.md §2 - first-class flows;
 * docs/FLOW.md "Access"). Gathered by the caller against its own substrate (the
 * write spine on a `tx`, the read layer on `db`), decided HERE - the one pure
 * resolver both paths share, so the gate and its read mirror cannot drift.
 */
export interface FlowRoleFacts {
  viewerRef: string;
  /** The flow's anchor (`group:` | `person:` | `item:` ref). */
  ownerRef: string;
  /** The viewer's (transitive) group reach, from `principalReach`. */
  groupsOfViewer: ReadonlyMap<string, Role>;
  /** Whether the viewer's direct paths count (`resolveViewerRole`'s `own`):
   *  a person anchor is a direct path too. */
  own: boolean;
  /** The flow's ACTIVE `definition_grants` (its audience). */
  grants: { granteeRef: string; role: Role }[];
  /** Item anchors only: does the viewer have OWNER reach on the anchor
   *  endeavor (an owner grant, or the seated requester)? Caller-gathered
   *  (`hasOwnerReach` over the anchor item's facts); ignored for group/person
   *  anchors. */
  anchorItemOwnerReach?: boolean;
}

/**
 * The viewer's role ON a flow - the pure core of `resolveFlowRole`
 * (src/server/definitions.ts) and the read layer's mirror
 * (src/read/flows.ts). A flow's authority is the UNION of two halves, highest
 * role wins:
 *
 * - **anchor reach** ⇒ `owner`: a PERSON anchor is the person themself; a
 *   GROUP anchor is any (transitive) member; an ITEM anchor is an owner of
 *   the endeavor (`anchorItemOwnerReach`).
 * - **the grant half**: the flow's audience, resolved through the SAME pure
 *   {@link resolveViewerRole} an item/view uses (a group grant bounded by the
 *   viewer's role in the group).
 *
 * `null` when neither half reaches.
 */
export function flowRoleFromFacts(facts: FlowRoleFacts): Role | null {
  let anchorRole: Role | null = null;
  if (facts.ownerRef.startsWith('person:')) {
    anchorRole = facts.own && facts.ownerRef === facts.viewerRef ? 'owner' : null;
  } else if (facts.ownerRef.startsWith('group:')) {
    anchorRole = facts.groupsOfViewer.has(facts.ownerRef) ? 'owner' : null;
  } else {
    anchorRole = facts.anchorItemOwnerReach ? 'owner' : null;
  }
  const { role: grantRole } = resolveViewerRole({
    viewerRef: facts.viewerRef,
    inheritedGrants: facts.grants.map((g) => ({
      granteeRef: g.granteeRef,
      role: g.role,
      sourceItemId: 'flow',
      inherited: false,
    })),
    groupsOfViewer: facts.groupsOfViewer,
    own: facts.own,
  });
  return maxRole(anchorRole, grantRole);
}
